import React, { useMemo, useState } from 'react';
import Select, { SingleValue } from 'react-select';

import { en } from 'constants/lang/en';
import { COUNTRIES } from 'constants/countries';

import { stringToArray } from 'utils/string';
import { generateReactSelectSkillGroupOptions, generateReactSelectOptionsFromObject } from 'utils/reactSelect';

import SkillsSelector from './SkillsSelector';
import type { DesignationRecord, EndLevelSkill, FlatSelectOption, ResourceFinderFilterQuery } from '../types';

export interface ResourceFilterProps {
  loadingSkills: boolean;
  skills: EndLevelSkill[];
  loadingDesignations: boolean;
  loadingAreas: boolean;
  designations: DesignationRecord[];
  areas: DesignationRecord[];
  initialState: ResourceFinderFilterQuery;
  onFilter: (payload: ResourceFinderFilterQuery) => void;
  onClear?: () => void;
}

interface ResourceFilterLocalState {
  skill: Array<number | string | null>;
  rating: Array<number | string | null>;
  designationId: string | number | null;
  country: string | null;
  areaId: string | number | null;
  rowIds: string[];
}

type CountryOption = {
  value: string;
  label: string;
};

let nextRowId = 0;
function generateRowId(): string {
  return `skill-row-${nextRowId++}`;
}

function scalarFromQuery(value: ResourceFinderFilterQuery[string]): string | number | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value === 'boolean') {
    return null;
  }
  if (Array.isArray(value)) {
    const first = value[0];

    if (first === undefined || first === null || typeof first === 'boolean') {
      return null;
    }

    return first;
  }

  return value;
}

function buildInitialFilterState(initialState: ResourceFinderFilterQuery): ResourceFilterLocalState {
  const skillRaw = initialState?.skill;
  const ratingRaw = initialState?.rating;

  const skillJoined =
    skillRaw === undefined || skillRaw === null ? '' : Array.isArray(skillRaw) ? skillRaw.join(',') : String(skillRaw);

  const ratingJoined =
    ratingRaw === undefined || ratingRaw === null
      ? ''
      : Array.isArray(ratingRaw)
        ? ratingRaw.join(',')
        : String(ratingRaw);

  const skills = skillJoined ? (stringToArray(skillJoined, ',') as string[]) : [];
  const ratings = ratingJoined ? (stringToArray(ratingJoined, ',') as string[]) : [];
  const skillCount = Math.max(skills.length || 1, ratings.length || 1);
  const countryRaw = scalarFromQuery(initialState?.country);

  const initialFilterState: ResourceFilterLocalState = {
    skill: skills.length ? skills : [null],
    rating: ratings.length ? ratings : [0],
    designationId: scalarFromQuery(initialState?.designationId),
    country: typeof countryRaw === 'string' ? countryRaw : null,
    areaId: scalarFromQuery(initialState?.areaId),
    rowIds: Array.from({ length: skillCount }, () => generateRowId()),
  };

  return initialFilterState;
}

const ResourceFilter: React.FC<ResourceFilterProps> = ({
  loadingSkills,
  skills,
  loadingDesignations,
  loadingAreas,
  designations,
  areas,
  initialState,
  onFilter,
  onClear,
}) => {
  const [filterState, setFilterState] = useState<ResourceFilterLocalState>(() => buildInitialFilterState(initialState));
  const [error, setError] = useState('');
  const countryOptions = useMemo<CountryOption[]>(
    () => COUNTRIES.map((country) => ({ label: country, value: country })),
    []
  );

  const handleChange = (
    name: keyof Pick<ResourceFilterLocalState, 'designationId' | 'areaId' | 'country'>,
    value: string | number | null
  ) => {
    setFilterState((prev) => ({ ...prev, [name]: value }));

    setError('');
  };

  const handleSkillChange = (index: number, value: number | string | undefined) => {
    setFilterState((prev) => {
      const newFilterState = { ...prev };
      if (newFilterState.skill.length >= index + 1) {
        newFilterState.skill[index] = value ?? null;
      }

      return newFilterState;
    });
    setError('');
  };

  const handleRatingChange = (index: number, value: number | null) => {
    setFilterState((prev) => {
      const newFilterState = { ...prev };
      if (newFilterState.rating.length >= index + 1) {
        newFilterState.rating[index] = value ?? 0;
      }

      return newFilterState;
    });
    setError('');
  };

  const handleSkillAddition = () => {
    setFilterState((prev) => {
      const newFilterState = { ...prev };
      newFilterState.skill.push(null);
      newFilterState.rating.push(0);
      newFilterState.rowIds.push(generateRowId());

      return newFilterState;
    });
    setError('');
  };

  const handleSkillRemove = (index: number) => {
    setFilterState((prev) => {
      const newFilterState = { ...prev };
      newFilterState.skill.splice(index, 1);
      newFilterState.rating.splice(index, 1);
      newFilterState.rowIds.splice(index, 1);

      return newFilterState;
    });
    setError('');
  };

  const handleClear = () => {
    setFilterState({
      skill: [null],
      rating: [0],
      designationId: null,
      country: null,
      areaId: null,
      rowIds: [generateRowId()],
    });
    setError('');
    if (onClear) {
      onClear();
    }
  };

  const handleFilter = () => {
    const { skill, rating: originalRating, designationId, country, areaId } = filterState;
    let valid = true;
    const normalizedSkill = [...skill];
    const normalizedRating = skill.map((_, index) => {
      const ratingValue = originalRating[index];
      const numericRating = Number(ratingValue ?? 0);
      return Number.isFinite(numericRating) ? numericRating : null;
    });

    normalizedSkill.forEach((skillItem, index) => {
      if (!skillItem && normalizedRating[index]) {
        setError(`Skill missing on row: ${index + 1}`);
        valid = false;
      } else if (!skillItem && !normalizedRating[index]) {
        normalizedRating[index] = null;
      }
    });

    if (valid) {
      let skillFiltered: Array<number | string> | null = normalizedSkill.filter((s) => s);
      let ratingFiltered: Array<number | string> | null = normalizedRating.filter((r) => r != null);
      if (!skillFiltered || !skillFiltered.length) {
        skillFiltered = null;
        ratingFiltered = null;
      }
      onFilter({ skill: skillFiltered, rating: ratingFiltered, designationId, country, areaId });
    }
  };

  const skillOptions = generateReactSelectSkillGroupOptions(skills);
  const designationOptions = generateReactSelectOptionsFromObject(designations);
  const areaOptions = generateReactSelectOptionsFromObject(areas);

  return (
    <>
      <div className="table-header name-wrap d-flex flex-row filter-header">
        <span className="resource-header-title">{'Filter By'}</span>
      </div>
      <div className="skills-selector">
        <SkillsSelector
          loading={loadingSkills}
          skillOptions={skillOptions}
          skills={filterState.skill}
          ratings={filterState.rating}
          rowIds={filterState.rowIds}
          onSkillChange={handleSkillChange}
          onRatingChange={handleRatingChange}
          onAddSkill={handleSkillAddition}
          onRemoveSkill={handleSkillRemove}
        />
        {error && <span className="color-red">{error}</span>}
      </div>
      <div className="designation-selector">
        <span className="filter-label font-12">{'Designation'}</span>
        <Select<FlatSelectOption>
          value={designationOptions.find((option) => option.value === +String(filterState.designationId ?? '')) ?? null}
          isSearchable={true}
          isClearable={true}
          isLoading={loadingDesignations}
          options={designationOptions}
          onChange={(option: SingleValue<FlatSelectOption>) => {
            handleChange('designationId', option?.value ?? null);
          }}
        />
      </div>
      <div className="designation-selector">
        <span className="filter-label font-12">{'Country'}</span>
        <Select<CountryOption>
          value={countryOptions.find(({ value }) => value === String(filterState.country ?? '')) ?? null}
          isSearchable={true}
          isClearable={true}
          options={countryOptions}
          onChange={(option: SingleValue<CountryOption>) => {
            handleChange('country', option?.value ?? null);
          }}
        />
      </div>
      <div className="designation-selector">
        <span className="filter-label font-12">{'Area'}</span>
        <Select<FlatSelectOption>
          value={areaOptions.find((option) => option.value === +String(filterState.areaId ?? '')) ?? null}
          isSearchable={true}
          isClearable={true}
          isLoading={loadingAreas}
          options={areaOptions}
          onChange={(option: SingleValue<FlatSelectOption>) => {
            handleChange('areaId', option?.value ?? null);
          }}
        />
      </div>
      <div className="filter-control d-flex flex-row">
        <button type="button" className="btn btn--primary" onClick={handleFilter}>
          {en.BUTTON.SEARCH}
        </button>
        <button type="button" className="btn btn--outlined-grey ml-10" onClick={handleClear}>
          {en.BUTTON.CLEAR}
        </button>
      </div>
    </>
  );
};

export default ResourceFilter;
